/* global AbortSignal, Blob, document, File, FormData, URL */
import { api } from '../../shared/api/client'
import type {
  DesignAssetCandidate, GingerbreadAsset, GingerbreadShape,
  GingerbreadShapeGeometryPreview, GingerbreadShapeMetrics, GingerbreadShapeUpload,
  GingerbreadShapeVersion, GingerbreadShapeVersionsResult,
  ContourPoints, GingerbreadSettings, GingerbreadSettingsState, ShapeFileKind,
  ShapeManufacturability, ShapeManufacturabilityIssue, ShapeRounding,
} from './types'

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}

export const unwrapGingerbreadResponse = <T,>(response: unknown): T => {
  const outer = asRecord(response)
  const envelope = asRecord(outer.data)
  return (envelope.data ?? outer.data) as T
}

const stringId = (value: unknown) => String(value ?? '')
const assetUrl = (id: string) => `/api/v1/gingerbread/assets/${id}/thumbnail`

const toAsset = (raw: unknown): GingerbreadAsset => {
  const value = asRecord(raw); const id = stringId(value.id); const metadata = asRecord(value.metadata)
  return {
    id, name: String(value.name ?? value.originalFileName ?? id),
    source: value.source === 'template' || value.source === 'upload' ? value.source : 'design',
    sourceDesignId: value.designCode == null ? null : String(value.designCode),
    url: assetUrl(id), thumbnailUrl: assetUrl(id), widthPx: Number(value.widthPx) || 0,
    heightPx: Number(value.heightPx) || 0, mimeType: value.mimeType == null ? null : String(value.mimeType),
    parentAssetId: value.parentAssetId == null ? null : stringId(value.parentAssetId),
    colorProfileConverted: Boolean(metadata.colorProfileConverted ?? metadata.color_profile_converted),
  }
}

const optionalNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const person = (raw: unknown): { id: number; name?: string | null } | null => {
  if (raw == null) return null
  const value = asRecord(raw)
  if (Object.keys(value).length) return { id: Number(value.id ?? value.userId ?? value.user_id), name: value.name == null ? null : String(value.name) }
  return Number.isFinite(Number(raw)) ? { id: Number(raw) } : null
}

const toShapeMetrics = (raw: unknown): GingerbreadShapeMetrics => {
  const value = asRecord(raw); const bbox = asRecord(value.bbox)
  const bboxWidth = optionalNumber(bbox.width ?? bbox.w); const bboxHeight = optionalNumber(bbox.height ?? bbox.h)
  const widthMm = optionalNumber(value.widthMm ?? value.width_mm); const heightMm = optionalNumber(value.heightMm ?? value.height_mm)
  const ratio = optionalNumber(value.aspectRatio ?? value.aspect_ratio)
    ?? (bboxWidth && bboxHeight ? bboxWidth / bboxHeight : widthMm && heightMm ? widthMm / heightMm : 0)
  return {
    sugarAreaCm2: optionalNumber(value.sugarAreaCm2 ?? value.sugar_area_cm2),
    doughAreaCm2: optionalNumber(value.doughAreaCm2 ?? value.dough_area_cm2),
    doughWeightG: optionalNumber(value.doughWeightG ?? value.dough_weight_g),
    perimeterMm: optionalNumber(value.perimeterMm ?? value.perimeter_mm),
    widthMm, heightMm,
    aspectRatio: ratio,
    bbox: bboxWidth != null && bboxHeight != null ? { x: Number(bbox.x) || 0, y: Number(bbox.y) || 0, width: bboxWidth, height: bboxHeight } : null,
    areaRatio: optionalNumber(value.areaRatio ?? value.area_ratio),
    solidity: optionalNumber(value.solidity),
    vertices: optionalNumber(value.vertices ?? value.vertexCount ?? value.vertex_count),
    contourCount: optionalNumber(value.contourCount ?? value.contour_count),
  }
}

const parsedJson = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  try { return JSON.parse(value) } catch { return {} }
}

/** Контур: список [x, y] в мм (допускаются {x, y}, JSON-строка и обёртка в список колец). */
const toContour = (raw: unknown): ContourPoints | null => {
  let value = parsedJson(raw)
  if (!Array.isArray(value) || !value.length) return null
  if (Array.isArray(value[0]) && Array.isArray((value[0] as unknown[])[0])) value = value[0]
  const points: ContourPoints = []
  for (const point of value as unknown[]) {
    const x = Array.isArray(point) ? Number(point[0]) : Number(asRecord(point).x)
    const y = Array.isArray(point) ? Number(point[1]) : Number(asRecord(point).y)
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null
    points.push([x, y])
  }
  return points.length >= 3 ? points : null
}

const toRounding = (raw: unknown): ShapeRounding | null => {
  const value = asRecord(raw)
  if (!Object.keys(value).length || typeof value.applied !== 'boolean') return null
  return {
    applied: value.applied,
    innerRadiusMm: optionalNumber(value.innerRadiusMm) ?? 0,
    outerRadiusMm: optionalNumber(value.outerRadiusMm) ?? 0,
    areaDeltaMm2: optionalNumber(value.areaDeltaMm2) ?? 0,
    areaDeltaPct: optionalNumber(value.areaDeltaPct) ?? 0,
    maxDeviationMm: optionalNumber(value.maxDeviationMm) ?? 0,
    skippedCode: value.skippedCode == null ? null : String(value.skippedCode),
  }
}

const toManufacturability = (raw: unknown): ShapeManufacturability | null => {
  const value = asRecord(parsedJson(raw))
  if (!Object.keys(value).length) return null
  const issues = (Array.isArray(value.issues) ? value.issues : []).map((entry): ShapeManufacturabilityIssue => {
    const row = asRecord(entry); const details = asRecord(row.details)
    return { code: String(row.code ?? ''), message: String(row.message ?? ''), details: Object.keys(details).length ? details : null }
  })
  return {
    ok: value.ok == null ? issues.length === 0 : Boolean(value.ok),
    minNeckMm: optionalNumber(value.minNeckMm ?? value.min_neck_mm),
    minInnerRadiusMm: optionalNumber(value.minInnerRadiusMm ?? value.min_inner_radius_mm),
    issues,
    rounding: toRounding(value.rounding),
  }
}

type NumericSettingKey = Exclude<keyof GingerbreadSettings, 'autoRoundCorners'>
const SETTINGS_KEYS: Array<[NumericSettingKey, string]> = [
  ['doughOffsetMm', 'dough_offset_mm'], ['doughWeightGPerCm2', 'dough_weight_g_per_cm2'], ['doughThicknessMm', 'dough_thickness_mm'],
  ['bakeSpreadPct', 'bake_spread_pct'], ['envelopeGapMm', 'envelope_gap_mm'], ['envelopeSmoothMm', 'envelope_smooth_mm'],
  ['envelopeGapMinMm', 'envelope_gap_min_mm'], ['minNeckMm', 'min_neck_mm'], ['minInnerRadiusMm', 'min_inner_radius_mm'],
  ['minOuterRadiusMm', 'min_outer_radius_mm'],
  ['minSizeMm', 'min_size_mm'], ['maxSizeMm', 'max_size_mm'], ['layoutGapMm', 'layout_gap_mm'],
  ['kitPackagingG', 'kit_packaging_g'], ['singlePackagingG', 'single_packaging_g'],
]

/** Настройки: сервис хранит JSON с ключами snake_case (как у ядра), наружу допускаем и camelCase. */
const toSettingsState = (raw: unknown): GingerbreadSettingsState => {
  const value = asRecord(raw); const source = asRecord(parsedJson(value.settings ?? value.settingsJson ?? value.settings_json ?? value))
  const settings = {} as GingerbreadSettings
  for (const [camel, snake] of SETTINGS_KEYS) settings[camel] = optionalNumber(source[camel] ?? source[snake]) ?? 0
  settings.autoRoundCorners = (source.autoRoundCorners ?? source.auto_round_corners) !== false  // нет ключа = включено, как по умолчанию
  return { version: Number(value.version) || 1, settings, updatedAt: value.updatedAt == null && value.updated_at == null ? null : String(value.updatedAt ?? value.updated_at) }
}

const shapeAssetUrl = (shapeId: string, kind: 'mask'|'safe'|'thumb', versionId?: string) => {
  const query = versionId ? `?versionId=${encodeURIComponent(versionId)}` : ''
  return `/api/v1/gingerbread/shapes/${shapeId}/assets/${kind}${query}`
}

const toShapeVersion = (raw: unknown, shapeId: string, fallbackVersion?: number): GingerbreadShapeVersion => {
  const value = asRecord(raw); const assets = asRecord(value.assets); const metricsRaw = value.metrics ?? value.metricsJson ?? value.metrics_json
  const id = stringId(value.id)
  return {
    id, version: Number(value.version) || fallbackVersion || 1,
    widthMm: Number(value.widthMm ?? value.width_mm) || 0,
    heightMm: Number(value.heightMm ?? value.height_mm) || 0,
    maskUrl: shapeAssetUrl(shapeId, 'mask', id || undefined),
    printAreaUrl: assets.mask || value.maskStoragePath || value.mask_storage_path ? shapeAssetUrl(shapeId, 'mask', id || undefined) : null,
    safeAreaUrl: assets.safe || value.safeStoragePath || value.safe_storage_path ? shapeAssetUrl(shapeId, 'safe', id || undefined) : null,
    thumbUrl: assets.thumbnail || assets.thumb || value.thumbnailStoragePath || value.thumbnail_storage_path ? shapeAssetUrl(shapeId, 'thumb', id || undefined) : null,
    geometrySource: (value.geometrySource ?? value.geometry_source) === 'approved' ? 'approved' : 'provisional',
    bleedMm: optionalNumber(value.bleedMm ?? value.bleed_mm), safeInsetMm: optionalNumber(value.safeInsetMm ?? value.safe_inset_mm),
    sourceKind: value.sourceKind == null && value.source_kind == null ? null : String(value.sourceKind ?? value.source_kind) as GingerbreadShapeVersion['sourceKind'],
    sourceSha256: value.sourceSha256 == null && value.source_sha256 == null ? null : String(value.sourceSha256 ?? value.source_sha256),
    metrics: metricsRaw == null ? null : toShapeMetrics(parsedJson(metricsRaw)),
    approvedBy: person(value.approvedBy ?? value.approved_by), approvedAt: value.approvedAt == null && value.approved_at == null ? null : String(value.approvedAt ?? value.approved_at),
    approvalNote: value.approvalNote == null && value.approval_note == null ? null : String(value.approvalNote ?? value.approval_note),
    note: value.note == null ? null : String(value.note), usedInSetsCount: Number(value.usedInSetsCount ?? value.used_in_sets_count ?? value.usageCount ?? value.usage_count) || 0,
    createdAt: value.createdAt == null && value.created_at == null ? null : String(value.createdAt ?? value.created_at),
    createdBy: person(value.createdBy ?? value.created_by),
    contour: toContour(value.contour ?? value.contourJson ?? value.contour_json),
    doughContour: toContour(value.doughContour ?? value.dough_contour ?? value.doughContourJson ?? value.dough_contour_json),
    manufacturability: toManufacturability(value.manufacturability ?? value.manufacturabilityJson ?? value.manufacturability_json),
    hasVector: value.hasVector == null && value.has_vector == null
      ? toContour(value.contour ?? value.contourJson ?? value.contour_json) != null
      : Boolean(value.hasVector ?? value.has_vector),
    cutter: value.cutter == null ? null : { ready: Boolean(asRecord(value.cutter).ready), paramsSha256: asRecord(value.cutter).paramsSha256 == null ? null : String(asRecord(value.cutter).paramsSha256) },
    metricsStale: Boolean(value.metricsStale ?? value.metrics_stale),
  }
}

const toVectorVersion = (raw: unknown): GingerbreadShape['vectorVersion'] => {
  const value = asRecord(raw); const id = stringId(value.id); const version = Number(value.version)
  return id && Number.isFinite(version) && version > 0 ? { id, version } : null
}

const toShape = (raw: unknown): GingerbreadShape => {
  const value = asRecord(raw); const currentRaw = value.version ?? value.currentVersionData ?? value.current_version_data
  const version = asRecord(currentRaw); const id = stringId(value.id)
  const versionId = stringId(version.id ?? value.currentVersionId ?? value.current_version_id)
  const currentVersion = Object.keys(version).length ? toShapeVersion({ ...version, id: versionId }, id, Number(value.currentVersion ?? value.current_version)) : null
  return {
    id, code: String(value.code ?? id), name: String(value.name ?? value.code ?? id),
    widthMm: currentVersion?.widthMm || Number(value.widthMm ?? value.width_mm) || 0,
    heightMm: currentVersion?.heightMm || Number(value.heightMm ?? value.height_mm) || 0,
    isActive: Boolean(value.isActive ?? value.is_active), currentVersionId: versionId,
    currentVersion, versionsCount: Number(value.versionsCount ?? value.versions_count) || undefined,
    vectorVersion: toVectorVersion(value.vectorVersion ?? value.vector_version),
    sortOrder: Number(value.sortOrder ?? value.sort_order) || 0,
    archivedAt: value.archivedAt == null && value.archived_at == null ? null : String(value.archivedAt ?? value.archived_at),
    updatedAt: value.updatedAt == null && value.updated_at == null ? null : String(value.updatedAt ?? value.updated_at),
    createdBy: person(value.createdBy ?? value.created_by),
    family: optionalText(value.family),
    familyStyle: optionalText(value.familyStyle ?? value.family_style),
    glyph: optionalText(value.glyph),
    occasions: Array.isArray(value.occasions) ? value.occasions.map(String).filter(Boolean) : [],
  }
}

const optionalText = (raw: unknown): string | null => (raw === null || raw === undefined || raw === '' ? null : String(raw))

const toGeometryPreview = (raw: unknown): GingerbreadShapeGeometryPreview => {
  const value = asRecord(raw); const generated = asRecord(value.preview); const warningRows = Array.isArray(value.warnings) ? value.warnings : []
  const svgDataUrl = (source: unknown): string | null => {
    if (typeof source !== 'string' || !source) return null
    return source.startsWith('data:') ? source : `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`
  }
  const thumbnailBase64 = generated.thumbnailBase64 ?? generated.thumbnail_base64
  const thumbnailMime = String(generated.thumbnailMimeType ?? generated.thumbnail_mime_type ?? 'image/png')
  return {
    uploadId: value.uploadId == null && value.upload_id == null ? undefined : String(value.uploadId ?? value.upload_id),
    metrics: toShapeMetrics(value.metrics ?? value),
    thumbUrl: typeof thumbnailBase64 === 'string' && thumbnailBase64 ? `data:${thumbnailMime};base64,${thumbnailBase64}` : null,
    maskUrl: svgDataUrl(generated.maskSvg ?? generated.mask_svg),
    safeUrl: svgDataUrl(generated.safeSvg ?? generated.safe_svg),
    warnings: warningRows.map(rawWarning => { const warning = asRecord(rawWarning); return { code: String(warning.code ?? rawWarning), message: String(warning.message ?? '') } }),
    errorCode: value.errorCode == null && value.error_code == null ? null : String(value.errorCode ?? value.error_code),
    errorMessage: value.errorMessage == null && value.error_message == null ? null : String(value.errorMessage ?? value.error_message),
    contour: toContour(value.contour ?? generated.contour),
    doughContour: toContour(value.doughContour ?? value.dough_contour ?? generated.doughContour),
    manufacturability: toManufacturability(value.manufacturability ?? generated.manufacturability),
  }
}

/** Семейство, гарнитура/знак цифры и поводы формы — для окна выбора и группировки справочника. */
export interface ShapeClassification {
  family?: string | null; familyStyle?: string | null; glyph?: string | null; occasions?: string[]
}

export const gingerbreadAPI = {
  async listShapes(includeArchived = false): Promise<GingerbreadShape[]> {
    const raw = unwrapGingerbreadResponse<unknown>(await api.get(includeArchived ? '/gingerbread/shapes?includeArchived=true' : '/gingerbread/shapes'))
    return Array.isArray(raw) ? raw.map(toShape) : []
  },
  async uploadShape(file: File, onProgress?: (percentage: number) => void): Promise<GingerbreadShapeUpload> {
    const body = new FormData(); body.append('file', file)
    const raw = asRecord(unwrapGingerbreadResponse(await api.post('/gingerbread/shapes/upload', body, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: event => onProgress?.(event.total ? Math.round(event.loaded / event.total * 100) : 0) })))
    return { uploadId: String(raw.upload_id ?? raw.uploadId), originalName: String(raw.original_name ?? raw.originalName ?? file.name), mime: String(raw.mime ?? file.type), sizeBytes: Number(raw.size_bytes ?? raw.sizeBytes ?? file.size) }
  },
  async previewShape(body: { uploadId: string; widthMm: number; heightMm: number; safeInsetMm?: number }): Promise<GingerbreadShapeGeometryPreview> {
    return toGeometryPreview(unwrapGingerbreadResponse(await api.post('/gingerbread/shapes/preview', { upload_id: body.uploadId, widthMm: body.widthMm, heightMm: body.heightMm, safeInsetMm: body.safeInsetMm })))
  },
  async createShape(body: { uploadId: string; code: string; name: string; widthMm: number; heightMm: number; bleedMm?: number; safeInsetMm?: number; sortOrder?: number; note?: string } & ShapeClassification): Promise<GingerbreadShapeVersionsResult> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post('/gingerbread/shapes', { upload_id: body.uploadId, code: body.code, name: body.name, widthMm: body.widthMm, heightMm: body.heightMm, bleedMm: body.bleedMm, safeInsetMm: body.safeInsetMm, sortOrder: body.sortOrder, note: body.note, family: body.family, familyStyle: body.familyStyle, glyph: body.glyph, occasions: body.occasions })))
    const shapeRaw = raw.shape ?? raw; const shape = toShape(shapeRaw); const versionRaw = raw.version ?? asRecord(shapeRaw).version
    return { shape, versions: versionRaw ? [toShapeVersion(versionRaw, shape.id)] : [] }
  },
  async createShapeVersion(shapeId: string, body: { uploadId: string; widthMm: number; heightMm: number; bleedMm?: number; safeInsetMm?: number; note?: string }): Promise<GingerbreadShapeVersionsResult> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`/gingerbread/shapes/${shapeId}/versions`, { upload_id: body.uploadId, widthMm: body.widthMm, heightMm: body.heightMm, bleedMm: body.bleedMm, safeInsetMm: body.safeInsetMm, note: body.note })))
    const shape = toShape(raw.shape ?? { id: shapeId }); const versionRaw = raw.version
    return { shape, versions: versionRaw ? [toShapeVersion(versionRaw, shapeId)] : [] }
  },
  async updateShape(shapeId: string, patch: { name?: string; isActive?: boolean; sortOrder?: number } & ShapeClassification): Promise<GingerbreadShape> {
    return toShape(unwrapGingerbreadResponse(await api.patch(`/gingerbread/shapes/${shapeId}`, patch)))
  },
  async setShapeCurrentVersion(shapeId: string, versionId: string): Promise<void> { await api.post(`/gingerbread/shapes/${shapeId}/current`, { versionId }) },
  /** `override` — утвердить несмотря на проблемы технологичности (комментарий обязателен). */
  async approveShapeVersion(shapeId: string, versionId: string, note: string, override?: boolean): Promise<void> {
    await api.post(`/gingerbread/shapes/${shapeId}/versions/${versionId}/approve`, override ? { note, override: true } : { note })
  },
  /** Без `versionId` — для текущей версии; с ним — пересчёт версии с вектором, посчитанной при других параметрах (`metricsStale`). */
  async recomputeShapeVector(shapeId: string, versionId?: string): Promise<GingerbreadShapeVersion> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`/gingerbread/shapes/${shapeId}/recompute-vector`, versionId ? { versionId } : {})))
    return toShapeVersion(raw.version ?? raw, shapeId)
  },
  async buildShapeCutter(shapeId: string, versionId: string): Promise<GingerbreadShapeVersion> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`/gingerbread/shapes/${shapeId}/versions/${versionId}/cutter`, {})))
    return toShapeVersion(raw.version ?? { ...raw, id: versionId }, shapeId)
  },
  async buildTrialSheet(shapeId: string, versionId: string): Promise<void> {
    await api.post(`/gingerbread/shapes/${shapeId}/versions/${versionId}/trial-sheet`, {})
  },
  /** Скачивает файл версии (STL, пробный лист JPG/PLT, SVG контура) через авторизованный запрос. */
  async downloadShapeFile(shapeId: string, versionId: string, kind: ShapeFileKind, fileName: string): Promise<void> {
    const response = await api.get(`/gingerbread/shapes/${shapeId}/versions/${versionId}/files/${kind}`, { responseType: 'blob' })
    const url = URL.createObjectURL(response.data as Blob)
    const link = document.createElement('a')
    link.href = url; link.download = fileName
    document.body.appendChild(link); link.click(); link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  },
  async getSettings(): Promise<GingerbreadSettingsState> {
    return toSettingsState(unwrapGingerbreadResponse(await api.get('/gingerbread/settings')))
  },
  /** Отправляем только переданные поля; ключи — snake_case, как в хранимом JSON настроек. */
  async updateSettings(patch: Partial<GingerbreadSettings>): Promise<GingerbreadSettingsState> {
    const settings: Record<string, number | boolean> = {}
    for (const [camel, snake] of SETTINGS_KEYS) if (patch[camel] != null) settings[snake] = patch[camel] as number
    if (patch.autoRoundCorners != null) settings.auto_round_corners = patch.autoRoundCorners
    return toSettingsState(unwrapGingerbreadResponse(await api.patch('/gingerbread/settings', { settings })))
  },
  async archiveShape(shapeId: string): Promise<void> { await api.post(`/gingerbread/shapes/${shapeId}/archive`, {}) },
  async listShapeVersions(shapeId: string): Promise<GingerbreadShapeVersionsResult> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.get(`/gingerbread/shapes/${shapeId}/versions`))); const shapeRaw = asRecord(raw.shape)
    const versionRows = Array.isArray(raw.versions) ? raw.versions : []
    const versions = versionRows.map(version => toShapeVersion(version, shapeId))
    const currentNumber = Number(shapeRaw.currentVersion ?? shapeRaw.current_version)
    const currentRaw = versionRows.find(version => Boolean(asRecord(version).isCurrent ?? asRecord(version).is_current))
      ?? versionRows.find(version => Number(asRecord(version).version) === currentNumber)
    const currentVersionId = currentRaw ? stringId(asRecord(currentRaw).id) : ''
    const shape = { ...toShape(shapeRaw), currentVersionId, currentVersion: versions.find(version => version.id === currentVersionId) ?? null }
    return { shape, versions }
  },
  async addAssetsFromDesigns(designs: DesignAssetCandidate[]): Promise<{ assets: GingerbreadAsset[]; failed: Array<{ designCode: string; code: string; message: string }> }> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post('/gingerbread/assets/from-designs', { designs: designs.map(item => item.payload) })))
    return {
      assets: (Array.isArray(raw.assets) ? raw.assets : []).map(entry => toAsset(asRecord(entry).asset ?? entry)),
      failed: (Array.isArray(raw.failed) ? raw.failed : []).map(entry => {
        const row = asRecord(entry)
        return { designCode: String(row.designCode || ''), code: String(row.code || ''), message: String(row.message || '') }
      }),
    }
  },
  async searchDesigns(search: string, themeNumber?: number, signal?: AbortSignal): Promise<Array<Record<string, unknown>>> {
    const response = await api.get('/designs', { params: { limit: 50, offset: 0, search, theme_number: themeNumber || undefined }, signal }); const raw = unwrapGingerbreadResponse<unknown>(response)
    if (Array.isArray(raw)) return raw as Array<Record<string, unknown>>; const record = asRecord(raw); return Array.isArray(record.designs) ? record.designs as Array<Record<string, unknown>> : []
  },
  async uploadAssets(files: File[], onProgress?: (percentage: number) => void): Promise<GingerbreadAsset[]> {
    const uploaded: GingerbreadAsset[] = []
    for (let index = 0; index < files.length; index += 1) {
      const body = new FormData(); body.append('file', files[index])
      const raw = asRecord(unwrapGingerbreadResponse(await api.post('/gingerbread/assets/upload', body, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: event => onProgress?.(Math.round(((index + (event.total ? event.loaded / event.total : 0)) / files.length) * 100)) })))
      uploaded.push(toAsset(raw.asset ?? raw))
    }
    return uploaded
  },
}
